import { Icon } from "@iconify/react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { languageSwitcherOptions, LanguageCode } from "../../localization/supportedLanguages";
import { setLanguage } from "../../redux/features/language/languageSlice";
import { RootState } from "../../redux/store";

type CalendarCell = {
  day: number | null;
  key: string;
  selectable: boolean;
};

type CalendarMonth = {
  key: string;
  monthIndex: number;
  year: number;
};

type DemoDuration = 45;

const calendarMonths: CalendarMonth[] = [
  { key: "2026-06", monthIndex: 5, year: 2026 },
  { key: "2026-07", monthIndex: 6, year: 2026 },
];

const startDateKey = "2026-06-10";

const makeDateKey = (year: number, monthIndex: number, day: number) =>
  `${year}-${String(monthIndex + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const buildMonthCells = (year: number, monthIndex: number): CalendarCell[] => {
  const firstDay = new Date(year, monthIndex, 1).getDay();
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: CalendarCell[] = [];

  for (let index = 0; index < firstDay; index += 1) {
    cells.push({ day: null, key: `empty-${year}-${monthIndex}-${index}`, selectable: false });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = new Date(year, monthIndex, day);
    const dayOfWeek = date.getDay();
    const key = makeDateKey(year, monthIndex, day);
    const isWeekday = dayOfWeek !== 0 && dayOfWeek !== 6;
    cells.push({ day, key, selectable: isWeekday && key >= startDateKey });
  }

  return cells;
};

const bookingCopy = {
  en: {
    nav: {
      back: "Back",
      languageLabel: "Switch language",
    },
    hero: {
      kicker: "Book a YouWo demo",
      title: "Choose a date and time for your team",
      body: "Select a 45 minute demo with our team. Times are shown in America/Toronto, from 10:00 AM to 9:15 PM",
    },
    summary: {
      label: "Selected session",
      empty: "Choose a date and time",
      timezone: "America/Toronto time",
      location: "Google Meet",
      withTeam: "with the YouWo team",
    },
    side: {
      title: "YouWo business demo",
      duration: "45 minutes",
      video: "Video call",
      audience: "For teams evaluating workflow automation",
    },
    calendar: {
      title: "Pick a date",
      weekdays: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      months: ["June", "July"],
      unavailable: "Unavailable",
    },
    availability: {
      durationLabel: "Demo length",
      timeLabel: "Available times",
      timezoneLabel: "America/Toronto",
      range: "Starts from 10:00 AM to 9:15 PM",
      selectTime: "Select",
    },
    details: {
      title: "Confirm your demo details",
      body: "Tell us who is joining and what workflow you want to discuss",
      name: "Name",
      email: "Email",
      guests: "Add guests",
      guestsHint: "Separate multiple emails with commas",
      note: "Additional note",
      notePlaceholder: "What workflow should we look at together?",
      confirm: "Request demo",
      back: "Back to calendar",
      submitting: "Sending request",
      confirmed: "Demo request sent",
      confirmedBody: "We emailed you that the request was received and sent info@youwo.ai an approval email. Once approved, you will receive a confirmed booking email",
      error: "We could not send the booking request. Please try again or email info@youwo.ai",
    },
  },
  zh: {
    nav: {
      back: "返回",
      languageLabel: "切换语言",
    },
    hero: {
      kicker: "预约 YouWo 演示",
      title: "选择适合你团队的日期和时间",
      body: "可以预约 45 分钟的团队演示，时间按 America/Toronto 显示，可预约时间为上午 10:00 到晚上 9:15",
    },
    summary: {
      label: "已选择时间",
      empty: "选择日期和时间",
      timezone: "America/Toronto 时间",
      location: "Google Meet",
      withTeam: "与 YouWo 团队",
    },
    side: {
      title: "YouWo 企业演示",
      duration: "45 分钟",
      video: "视频会议",
      audience: "适合正在评估工作流自动化的团队",
    },
    calendar: {
      title: "选择日期",
      weekdays: ["日", "一", "二", "三", "四", "五", "六"],
      months: ["6月", "7月"],
      unavailable: "不可选",
    },
    availability: {
      durationLabel: "演示时长",
      timeLabel: "可预约时间",
      timezoneLabel: "America/Toronto",
      range: "从上午 10:00 到晚上 9:15",
      selectTime: "选择",
    },
    details: {
      title: "确认演示信息",
      body: "告诉我们谁会参加，以及你想讨论哪个工作流",
      name: "姓名",
      email: "邮箱",
      guests: "添加来宾",
      guestsHint: "多个邮箱请用逗号分隔",
      note: "补充说明",
      notePlaceholder: "你想一起看哪个工作流",
      confirm: "请求演示",
      back: "返回日历",
      submitting: "正在发送请求",
      confirmed: "演示请求已发送",
      confirmedBody: "我们已向你发送收到请求的邮件，并向 info@youwo.ai 发送审批邮件。审批通过后，你会收到正式确认邮件",
      error: "预约请求发送失败，请重试或发送邮件到 info@youwo.ai",
    },
  },
  ja: {
    nav: {
      back: "戻る",
      languageLabel: "言語を切り替え",
    },
    hero: {
      kicker: "YouWo デモを予約",
      title: "チームに合う日時を選択",
      body: "45 分のチームデモを予約できます、時間は America/Toronto 表示で、10:00 AM から 9:15 PM まで選択できます",
    },
    summary: {
      label: "選択中のセッション",
      empty: "日付と時間を選択",
      timezone: "America/Toronto 時間",
      location: "Google Meet",
      withTeam: "YouWo チームと",
    },
    side: {
      title: "YouWo ビジネスデモ",
      duration: "45 分",
      video: "ビデオ通話",
      audience: "ワークフロー自動化を検討するチーム向け",
    },
    calendar: {
      title: "日付を選択",
      weekdays: ["日", "月", "火", "水", "木", "金", "土"],
      months: ["6月", "7月"],
      unavailable: "選択不可",
    },
    availability: {
      durationLabel: "デモ時間",
      timeLabel: "予約可能な時間",
      timezoneLabel: "America/Toronto",
      range: "10:00 AM から 9:15 PM まで",
      selectTime: "選択",
    },
    details: {
      title: "デモ情報を確認",
      body: "参加者と相談したいワークフローを教えてください",
      name: "名前",
      email: "メール",
      guests: "ゲストを追加",
      guestsHint: "複数のメールはカンマで区切ってください",
      note: "追加メモ",
      notePlaceholder: "一緒に確認したいワークフロー",
      confirm: "デモをリクエスト",
      back: "カレンダーへ戻る",
      submitting: "リクエスト送信中",
      confirmed: "デモリクエストを送信しました",
      confirmedBody: "リクエスト受領メールをあなたに送り、info@youwo.ai に承認メールを送りました。承認後、正式な予約確認メールが届きます",
      error: "予約リクエストを送信できませんでした。もう一度お試しいただくか info@youwo.ai にご連絡ください",
    },
  },
} satisfies Record<LanguageCode, {
  nav: Record<string, string>;
  hero: Record<string, string>;
  summary: Record<string, string>;
  side: Record<string, string>;
  calendar: {
    title: string;
    weekdays: string[];
    months: string[];
    unavailable: string;
  };
  availability: Record<string, string>;
  details: Record<string, string>;
}>;

const durationLabel = (duration: DemoDuration, language: LanguageCode) => {
  if (language === "zh") {
    return `${duration} 分钟演示`;
  }

  if (language === "ja") {
    return `${duration} 分デモ`;
  }

  return `${duration} minute demo`;
};

const selectedDateText = (
  selectedDateKey: string,
  selectedTime: string,
  language: LanguageCode,
  copy: typeof bookingCopy.en,
) => {
  const [, month, day] = selectedDateKey.split("-");
  const monthNumber = Number(month);
  const dayNumber = Number(day);

  if (language === "en") {
    const monthName = monthNumber === 6 ? "June" : "July";
    return `${monthName} ${dayNumber}, ${selectedTime}`;
  }

  if (language === "zh") {
    return `${monthNumber}月${dayNumber}日 ${selectedTime}`;
  }

  return `${monthNumber}月${dayNumber}日 ${selectedTime}`;
};

const timeSlots = [
  "10:00 AM",
  "10:45 AM",
  "11:30 AM",
  "12:15 PM",
  "1:00 PM",
  "1:45 PM",
  "2:30 PM",
  "3:15 PM",
  "4:00 PM",
  "4:45 PM",
  "5:30 PM",
  "6:15 PM",
  "7:00 PM",
  "7:45 PM",
  "8:30 PM",
  "9:15 PM",
];

const buildApiUrl = (path: string) => {
  const cleanPath = path.replace(/^\/+/, "");
  const base = process.env.REACT_APP_API_ENDPOINT;

  if (!base) {
    return `/${cleanPath}`;
  }

  return `${base.replace(/\/+$/, "")}/${cleanPath}`;
};

const YouWoDemoBooking: React.FC = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useSelector((state: RootState) => state.language.current);
  const copy = bookingCopy[currentLanguage] ?? bookingCopy.en;
  const [languageOpen, setLanguageOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(startDateKey);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [step, setStep] = useState<"calendar" | "details">("calendar");
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const selectedDateLabel = selectedTime
    ? selectedDateText(selectedDate, selectedTime, currentLanguage, copy)
    : copy.summary.empty;
  const selectedDuration: DemoDuration = 45;
  const selectedDurationLabel = durationLabel(selectedDuration, currentLanguage);

  const handleLanguageChange = (language: LanguageCode) => {
    dispatch(setLanguage(language));
    i18n.changeLanguage(language);
    setLanguageOpen(false);
  };

  const handleDateSelect = (dateKey: string) => {
    setSelectedDate(dateKey);
    setSelectedTime(null);
    setStep("calendar");
    setConfirmed(false);
    setSubmitError(null);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setStep("details");
    setConfirmed(false);
    setSubmitError(null);
  };

  const handleDemoRequestSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedTime) {
      return;
    }

    const formData = new FormData(event.currentTarget);
    const payload = {
      name: String(formData.get("name") || "").trim(),
      email: String(formData.get("email") || "").trim(),
      guests: String(formData.get("guests") || "").trim(),
      note: String(formData.get("note") || "").trim(),
      selectedDate,
      selectedTime,
      durationMinutes: selectedDuration,
      timezone: "America/Toronto",
      language: currentLanguage,
    };

    setIsSubmitting(true);
    setSubmitError(null);
    setConfirmed(false);

    try {
      const response = await fetch(buildApiUrl("api/v1/demo-bookings/request"), {
        body: JSON.stringify(payload),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(`Demo booking request failed with status ${response.status}`);
      }

      setConfirmed(true);
    } catch {
      setSubmitError(copy.details.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedMonthLabel = useMemo(() => {
    const [, month] = selectedDate.split("-");
    return Number(month) === 6 ? copy.calendar.months[0] : copy.calendar.months[1];
  }, [copy.calendar.months, selectedDate]);

  return (
    <main className="yw-booking-page" data-lang={currentLanguage}>
      <style>{bookingStyles}</style>

      <section className="yw-booking-shell">
        <div className="yw-booking-topbar">
          <a className="yw-booking-back" href="/internal/youwo-business-landing">
            <Icon icon="lucide:arrow-left" />
            {copy.nav.back}
          </a>
          <div className="yw-booking-language-menu">
            <button
              aria-expanded={languageOpen}
              aria-label={copy.nav.languageLabel}
              className="yw-booking-language-trigger"
              onClick={() => setLanguageOpen((isOpen) => !isOpen)}
              type="button"
            >
              {languageSwitcherOptions.find((option) => option.code === currentLanguage)?.shortLabel ?? "EN"}
              <Icon icon="lucide:chevron-down" />
            </button>
            {languageOpen ? (
              <div className="yw-booking-language-popover">
                {languageSwitcherOptions.map((option) => (
                  <button
                    className={option.code === currentLanguage ? "yw-booking-language-option yw-booking-language-option-active" : "yw-booking-language-option"}
                    key={option.code}
                    onClick={() => handleLanguageChange(option.code)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="yw-booking-hero">
          <div>
            <p className="yw-booking-kicker">{copy.hero.kicker}</p>
            <h1>{copy.hero.title}</h1>
            <p>{copy.hero.body}</p>
          </div>
          <div className="yw-booking-summary" aria-label="Selected demo time">
            <span>{copy.summary.label}</span>
            <strong>{selectedDateLabel}</strong>
            <p>
              {selectedDurationLabel} · {copy.summary.timezone} · {copy.summary.location}
            </p>
          </div>
        </div>

        <div className="yw-booking-card">
          <aside className="yw-booking-side">
            <div className="yw-booking-logo">
              <span><Icon icon="lucide:feather" /></span>
              <strong>YouWo</strong>
            </div>
            <h2>{copy.side.title}</h2>
            <ul>
              <li><Icon icon="lucide:clock" />{copy.side.duration}</li>
              <li><Icon icon="lucide:video" />{copy.side.video}</li>
              <li><Icon icon="lucide:map-pin" />{copy.summary.timezone}</li>
              <li><Icon icon="lucide:users" />{copy.side.audience}</li>
            </ul>
          </aside>

          {step === "calendar" ? (
            <>
              <section className="yw-booking-calendar" aria-label="Calendar booking options">
                <div className="yw-booking-section-heading">
                  <p>{copy.calendar.title}</p>
                  <span>{selectedMonthLabel} · {copy.summary.timezone}</span>
                </div>

                <div className="yw-month-stack">
                  {calendarMonths.map((month, monthIndex) => (
                    <div className="yw-month-panel" key={month.key}>
                      <div className="yw-month-title">{copy.calendar.months[monthIndex]} 2026</div>
                      <div className="yw-weekday-grid">
                        {copy.calendar.weekdays.map((weekday) => (
                          <span key={`${month.key}-${weekday}`}>{weekday}</span>
                        ))}
                      </div>
                      <div className="yw-calendar-grid">
                        {buildMonthCells(month.year, month.monthIndex).map((cell) => (
                          <button
                            aria-label={cell.day ? `${copy.calendar.months[monthIndex]} ${cell.day}` : copy.calendar.unavailable}
                            className={[
                              "yw-calendar-day",
                              cell.day ? "" : "yw-calendar-day-empty",
                              cell.selectable ? "yw-calendar-day-selectable" : "yw-calendar-day-disabled",
                              cell.key === selectedDate ? "yw-calendar-day-active" : "",
                            ].filter(Boolean).join(" ")}
                            disabled={!cell.day || !cell.selectable}
                            key={cell.key}
                            onClick={() => handleDateSelect(cell.key)}
                            type="button"
                          >
                            {cell.day}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <aside className="yw-booking-details">
                <div className="yw-booking-section-heading">
                  <p>{copy.availability.durationLabel}</p>
                  <span>{copy.availability.timezoneLabel}</span>
                </div>
                <div className="yw-duration-static">
                  <Icon icon="lucide:clock" />
                  <span>{selectedDurationLabel}</span>
                </div>

                <div className="yw-booking-section-heading yw-time-heading">
                  <p>{copy.availability.timeLabel}</p>
                  <span>{copy.availability.range}</span>
                </div>
                <div className="yw-time-grid">
                  {timeSlots.map((slot) => (
                    <button
                      className={slot === selectedTime ? "yw-time-pill yw-time-pill-active" : "yw-time-pill"}
                      key={slot}
                      onClick={() => handleTimeSelect(slot)}
                      type="button"
                    >
                      <strong>{slot}</strong>
                      <span>{copy.availability.selectTime}</span>
                    </button>
                  ))}
                </div>
              </aside>
            </>
          ) : (
            <section className="yw-booking-form-stage">
              <div className="yw-form-summary">
                <span>{copy.summary.label}</span>
                <strong>{selectedDateLabel}</strong>
                <p>{selectedDurationLabel} · {copy.summary.withTeam} · {copy.summary.timezone}</p>
              </div>

              <form className="yw-booking-form" onSubmit={handleDemoRequestSubmit}>
                <div>
                  <p className="yw-form-title">{copy.details.title}</p>
                  <span className="yw-form-subtitle">{copy.details.body}</span>
                </div>
                <label>
                  <span>{copy.details.name}</span>
                  <input name="name" required type="text" />
                </label>
                <label>
                  <span>{copy.details.email}</span>
                  <input name="email" required type="email" />
                </label>
                <label>
                  <span>{copy.details.guests}</span>
                  <input name="guests" type="text" />
                </label>
                <label>
                  <span>{copy.details.note}</span>
                  <textarea name="note" rows={4} />
                </label>
                <div className="yw-form-actions">
                  <button
                    className="yw-form-secondary"
                    disabled={isSubmitting}
                    onClick={() => {
                      setStep("calendar");
                      setSubmitError(null);
                    }}
                    type="button"
                  >
                    {copy.details.back}
                  </button>
                  <button className="yw-form-primary" disabled={isSubmitting} type="submit">
                    {isSubmitting ? copy.details.submitting : copy.details.confirm}
                  </button>
                </div>
                {submitError ? (
                  <div className="yw-error-message">
                    <Icon icon="lucide:circle-alert" />
                    <span>{submitError}</span>
                  </div>
                ) : null}
                {confirmed ? (
                  <div className="yw-confirmed-message">
                    <Icon icon="lucide:check" />
                    <div>
                      <strong>{copy.details.confirmed}</strong>
                      <span>{copy.details.confirmedBody}</span>
                    </div>
                  </div>
                ) : null}
              </form>
            </section>
          )}
        </div>
      </section>
    </main>
  );
};

const bookingStyles = `
@import url("https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&display=swap");

.yw-booking-page {
  --ink: #1d261f;
  --muted: #687066;
  --line: rgba(45, 58, 48, 0.14);
  --paper: #fffdf7;
  --sage: #dbe8d3;
  --moss: #78906c;
  --mint: #dff2ea;
  --peach: #f7d9c9;
  --butter: #f7edc7;
  --lavender: #ebe7fb;
  background:
    linear-gradient(180deg, rgba(219, 238, 250, 0.72), rgba(255, 253, 247, 0.36) 48%, rgba(219, 232, 211, 0.38)),
    var(--paper);
  color: var(--ink);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  min-height: 100vh;
  overflow-x: hidden;
}

.yw-booking-page * {
  box-sizing: border-box;
}

#webpack-dev-server-client-overlay {
  display: none !important;
  pointer-events: none !important;
}

.yw-booking-page a {
  color: inherit;
  text-decoration: none;
}

.yw-booking-page button,
.yw-booking-page input,
.yw-booking-page textarea {
  font: inherit;
}

.yw-booking-shell {
  margin: 0 auto;
  max-width: 1240px;
  padding: 28px 24px 64px;
}

.yw-booking-topbar {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.yw-booking-back,
.yw-booking-language-trigger {
  align-items: center;
  background: rgba(255, 253, 247, 0.74);
  border: 1px solid var(--line);
  border-radius: 999px;
  display: inline-flex;
  gap: 8px;
  min-height: 44px;
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.yw-booking-back {
  font-size: 14px;
  font-weight: 760;
  padding: 0 16px;
}

.yw-booking-language-menu {
  position: relative;
}

.yw-booking-language-trigger {
  color: #65725f;
  cursor: pointer;
  font-size: 13px;
  font-weight: 780;
  padding: 0 14px;
  white-space: nowrap;
}

.yw-booking-language-trigger svg {
  font-size: 14px;
}

.yw-booking-language-popover {
  background: rgba(255, 253, 247, 0.96);
  border: 1px solid var(--line);
  border-radius: 18px;
  box-shadow: 0 18px 44px rgba(74, 83, 68, 0.14);
  display: grid;
  gap: 3px;
  min-width: 132px;
  padding: 7px;
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  z-index: 20;
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.yw-booking-language-option {
  background: transparent;
  border: 0;
  border-radius: 12px;
  color: #65725f;
  cursor: pointer;
  font-size: 13px;
  font-weight: 780;
  min-height: 34px;
  padding: 0 10px;
  text-align: left;
  white-space: nowrap;
  width: 100%;
}

.yw-booking-language-option-active {
  background: #172019;
  color: #fffdf7;
}

.yw-booking-hero {
  align-items: end;
  display: grid;
  gap: 28px;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.42fr);
  padding: 72px 0 34px;
}

.yw-booking-kicker,
.yw-booking-section-heading p,
.yw-booking-summary span,
.yw-form-summary span {
  color: #65725f;
  font-size: 12px;
  font-weight: 840;
  letter-spacing: 0.12em;
  margin: 0;
  text-transform: uppercase;
}

.yw-booking-hero h1 {
  font-family: "Playfair Display", Georgia, serif;
  font-size: clamp(56px, 6.2vw, 92px);
  font-weight: 400;
  letter-spacing: 0;
  line-height: 0.98;
  margin: 18px 0 0;
  max-width: 900px;
}

.yw-booking-page[data-lang="zh"] .yw-booking-hero h1,
.yw-booking-page[data-lang="ja"] .yw-booking-hero h1 {
  font-size: clamp(48px, 5.4vw, 76px);
  line-height: 1.08;
  max-width: 760px;
}

.yw-booking-hero > div:first-child > p:not(.yw-booking-kicker) {
  color: #4f584d;
  font-size: clamp(18px, 1.45vw, 21px);
  line-height: 1.55;
  margin: 26px 0 0;
  max-width: 760px;
}

.yw-booking-summary {
  background: rgba(255, 253, 247, 0.78);
  border: 1px solid var(--line);
  border-radius: 28px;
  box-shadow: 0 24px 70px rgba(79, 93, 77, 0.12);
  padding: 24px;
}

.yw-booking-summary strong,
.yw-form-summary strong {
  display: block;
  font-family: "Playfair Display", Georgia, serif;
  font-size: 34px;
  font-weight: 400;
  line-height: 1;
  margin-top: 18px;
}

.yw-booking-summary p,
.yw-form-summary p {
  color: #5d665a;
  font-size: 15px;
  line-height: 1.45;
  margin: 16px 0 0;
}

.yw-booking-card {
  background: rgba(255, 253, 247, 0.72);
  border: 1px solid rgba(45, 58, 48, 0.14);
  border-radius: 34px;
  box-shadow: 0 34px 100px rgba(79, 93, 77, 0.16);
  display: grid;
  grid-template-columns: minmax(260px, 0.72fr) minmax(460px, 1.16fr) minmax(300px, 0.82fr);
  overflow: hidden;
  -webkit-backdrop-filter: blur(18px);
  backdrop-filter: blur(18px);
}

.yw-booking-side,
.yw-booking-calendar,
.yw-booking-details,
.yw-booking-form-stage {
  padding: 28px;
}

.yw-booking-side {
  background:
    linear-gradient(160deg, rgba(219, 232, 211, 0.54), rgba(247, 217, 201, 0.34)),
    rgba(255, 255, 255, 0.38);
  border-right: 1px solid var(--line);
}

.yw-booking-logo {
  align-items: center;
  display: flex;
  gap: 10px;
}

.yw-booking-logo span {
  align-items: center;
  background: #172019;
  border-radius: 999px;
  color: #fffdf7;
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.yw-booking-logo strong {
  font-size: 18px;
  font-weight: 820;
}

.yw-booking-side h2 {
  font-family: "Playfair Display", Georgia, serif;
  font-size: 48px;
  font-weight: 400;
  line-height: 1;
  margin: 64px 0 0;
}

.yw-booking-side ul {
  display: grid;
  gap: 12px;
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
}

.yw-booking-side li {
  align-items: center;
  color: #4f584d;
  display: flex;
  font-size: 15px;
  font-weight: 720;
  gap: 10px;
  line-height: 1.35;
}

.yw-booking-side li svg {
  color: #6f8f6b;
  flex: 0 0 auto;
}

.yw-booking-calendar {
  border-right: 1px solid var(--line);
}

.yw-booking-section-heading {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
}

.yw-booking-section-heading span {
  color: #7a866f;
  font-size: 13px;
  font-weight: 720;
}

.yw-month-stack {
  display: grid;
  gap: 18px;
  margin-top: 20px;
}

.yw-month-panel {
  background: rgba(255, 255, 255, 0.52);
  border: 1px solid rgba(45, 58, 48, 0.1);
  border-radius: 24px;
  padding: 18px;
}

.yw-month-title {
  color: #2f3a30;
  font-size: 17px;
  font-weight: 820;
  margin-bottom: 16px;
}

.yw-weekday-grid,
.yw-calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.yw-weekday-grid {
  gap: 4px;
  margin-bottom: 8px;
}

.yw-weekday-grid span {
  color: #8a9485;
  font-size: 11px;
  font-weight: 780;
  text-align: center;
}

.yw-calendar-grid {
  gap: 6px;
}

.yw-calendar-day {
  align-items: center;
  background: rgba(255, 253, 247, 0.7);
  border: 1px solid rgba(45, 58, 48, 0.1);
  border-radius: 12px;
  color: #2f3a30;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 760;
  height: 40px;
  justify-content: center;
  padding: 0;
}

.yw-calendar-day-empty {
  background: transparent;
  border-color: transparent;
}

.yw-calendar-day-disabled {
  color: rgba(47, 58, 48, 0.24);
  cursor: not-allowed;
}

.yw-calendar-day-selectable:hover {
  border-color: rgba(111, 143, 107, 0.5);
  transform: translateY(-1px);
}

.yw-calendar-day-active {
  background: #172019;
  color: #fffdf7;
}

.yw-duration-static {
  align-items: center;
  background: #172019;
  border-radius: 18px;
  color: #fffdf7;
  display: grid;
  gap: 10px;
  grid-template-columns: auto 1fr;
  margin-top: 18px;
  min-height: 58px;
  padding: 0 16px;
}

.yw-duration-static svg {
  color: rgba(255, 253, 247, 0.76);
  font-size: 18px;
}

.yw-duration-static span {
  font-size: 15px;
  font-weight: 820;
}

.yw-time-pill,
.yw-form-secondary,
.yw-form-primary {
  border: 0;
  cursor: pointer;
}

.yw-time-heading {
  margin-top: 28px;
}

.yw-time-grid {
  display: grid;
  gap: 10px;
  margin-top: 18px;
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
}

.yw-time-pill {
  align-items: center;
  background: rgba(255, 255, 255, 0.62);
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 18px;
  color: #2f3a30;
  display: flex;
  justify-content: space-between;
  min-height: 54px;
  padding: 0 15px;
  text-align: left;
}

.yw-time-pill:hover {
  border-color: rgba(111, 143, 107, 0.5);
  transform: translateY(-1px);
}

.yw-time-pill strong {
  font-size: 15px;
  font-weight: 820;
}

.yw-time-pill span {
  color: #7a866f;
  font-size: 12px;
  font-weight: 760;
}

.yw-time-pill-active {
  background: #172019;
  color: #fffdf7;
}

.yw-time-pill-active span {
  color: rgba(255, 253, 247, 0.62);
}

.yw-booking-form-stage {
  display: grid;
  gap: 24px;
  grid-column: span 2;
  grid-template-columns: minmax(260px, 0.8fr) minmax(360px, 1fr);
}

.yw-form-summary {
  align-self: start;
  background: #172019;
  border-radius: 28px;
  color: #fffdf7;
  padding: 26px;
}

.yw-form-summary span {
  color: rgba(255, 253, 247, 0.58);
}

.yw-form-summary p {
  color: rgba(255, 253, 247, 0.7);
}

.yw-booking-form {
  display: grid;
  gap: 16px;
}

.yw-form-title {
  color: #2f3a30;
  font-family: "Playfair Display", Georgia, serif;
  font-size: 48px;
  font-weight: 400;
  line-height: 1;
  margin: 0;
}

.yw-form-subtitle {
  color: #687066;
  display: block;
  font-size: 15px;
  line-height: 1.45;
  margin-top: 14px;
}

.yw-booking-form label {
  display: grid;
  gap: 8px;
}

.yw-booking-form label > span {
  color: #4f584d;
  font-size: 13px;
  font-weight: 820;
}

.yw-booking-form input,
.yw-booking-form textarea {
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(45, 58, 48, 0.14);
  border-radius: 16px;
  color: #1d261f;
  outline: none;
  padding: 14px 15px;
  width: 100%;
}

.yw-booking-form textarea {
  resize: vertical;
}

.yw-form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.yw-form-secondary,
.yw-form-primary {
  border-radius: 999px;
  font-size: 15px;
  font-weight: 820;
  min-height: 48px;
  padding: 0 22px;
}

.yw-form-secondary {
  background: rgba(255, 255, 255, 0.58);
  border: 1px solid var(--line);
  color: #2f3a30;
}

.yw-form-primary {
  background: #172019;
  color: #fffdf7;
}

.yw-form-secondary:disabled,
.yw-form-primary:disabled {
  cursor: progress;
  opacity: 0.62;
}

.yw-error-message,
.yw-confirmed-message {
  align-items: flex-start;
  border: 1px solid rgba(45, 58, 48, 0.12);
  border-radius: 18px;
  color: #2f3a30;
  display: flex;
  gap: 12px;
  padding: 16px;
}

.yw-error-message {
  background: rgba(247, 217, 201, 0.52);
}

.yw-confirmed-message {
  background: rgba(219, 232, 211, 0.54);
}

.yw-error-message svg,
.yw-confirmed-message svg {
  flex: 0 0 auto;
  margin-top: 2px;
}

.yw-error-message svg {
  color: #a46756;
}

.yw-confirmed-message svg {
  color: #6f8f6b;
}

.yw-confirmed-message div {
  display: grid;
  gap: 4px;
}

.yw-confirmed-message strong {
  font-size: 15px;
  font-weight: 820;
}

.yw-confirmed-message span {
  color: #5d665a;
  font-size: 14px;
  line-height: 1.45;
}

.yw-error-message span {
  color: #74493b;
  font-size: 14px;
  font-weight: 720;
  line-height: 1.45;
}

@media (prefers-reduced-motion: no-preference) {
  .yw-booking-hero,
  .yw-booking-card {
    animation: ywBookingRise 820ms cubic-bezier(0.19, 1, 0.22, 1) both;
  }

  .yw-booking-card {
    animation-delay: 120ms;
  }
}

@keyframes ywBookingRise {
  0% {
    opacity: 0;
    transform: translateY(22px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1120px) {
  .yw-booking-hero,
  .yw-booking-card,
  .yw-booking-form-stage {
    grid-template-columns: 1fr;
  }

  .yw-booking-side,
  .yw-booking-calendar {
    border-right: 0;
    border-bottom: 1px solid var(--line);
  }

  .yw-booking-form-stage {
    grid-column: auto;
  }

}

@media (max-width: 640px) {
  .yw-booking-shell {
    padding: 16px 14px 40px;
  }

  .yw-booking-topbar {
    align-items: flex-start;
  }

  .yw-booking-language-trigger {
    font-size: 12px;
    min-height: 40px;
    padding: 0 11px;
  }

  .yw-booking-language-option {
    font-size: 11px;
    min-height: 30px;
    padding: 0 7px;
  }

  .yw-booking-hero {
    padding: 56px 0 26px;
  }

  .yw-booking-hero h1 {
    font-size: clamp(44px, 14vw, 60px);
  }

  .yw-booking-page[data-lang="zh"] .yw-booking-hero h1,
  .yw-booking-page[data-lang="ja"] .yw-booking-hero h1 {
    font-size: clamp(36px, 10.5vw, 48px);
    line-height: 1.1;
  }

  .yw-booking-card {
    border-radius: 26px;
  }

  .yw-booking-side,
  .yw-booking-calendar,
  .yw-booking-details,
  .yw-booking-form-stage {
    padding: 18px;
  }

  .yw-booking-side h2,
  .yw-form-title {
    font-size: 38px;
  }

  .yw-month-panel {
    padding: 14px;
  }

  .yw-calendar-day {
    border-radius: 10px;
    font-size: 12px;
    height: 34px;
  }

  .yw-time-grid {
    max-height: none;
    overflow: visible;
  }

  .yw-form-actions {
    justify-content: stretch;
  }

  .yw-form-secondary,
  .yw-form-primary {
    width: 100%;
  }
}
`;

export default YouWoDemoBooking;
